import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';
import { HelpCircle, Sparkles } from 'lucide-react';
import { useLeadModal } from '../../lib/leadModalContext';
import { SCHOOL } from '../../lib/constants';

const FAQS = [
  {
    q: 'What ages do you teach at Universal Kempo Karate?',
    a: "We teach kids as young as 4 in our Little Dragons program and welcome students of every age through our Adult classes. Kids (7-12), Teens (13-17), and Adult (all levels) each train together with age-matched instructors so everyone progresses at the right pace.",
  },
  {
    q: 'How much do classes cost?',
    a: "We keep tuition simple and family-friendly. Because pricing depends on the program and how many family members you enroll, we quote it during your free trial so we can build the best fit for you. Tap \u201CBook Free Trial\u201D or call " + SCHOOL.phone + " and we\u2019ll walk you through it.",
  },
  {
    q: 'Do you offer a free trial class?',
    a: "Yes \u2014 your first two classes are on us, and new students who enroll also receive a free uniform (gi). No commitment, no pressure. Just come train, meet the instructors, and see if it\u2019s the right fit.",
  },
  {
    q: 'What is Kempo Karate and how is it different?',
    a: "Kempo \u2014 the \u201CWay of the Fist\u201D \u2014 is a traditional martial art that blends striking (hands, elbows, knees, kicks), blocks, joint locks, and self-defense flow drills. Unlike pure sport karate, Kempo is built for real-world self-defense while still developing discipline, focus, and fitness.",
  },
  {
    q: 'Is karate safe for young kids?',
    a: "Absolutely. Our Little Dragons and Kids classes are designed around age-appropriate movement, listening games, and character development. All contact is controlled and instructor-supervised, and our head instructors have decades of experience training children safely.",
  },
  {
    q: 'What should my child wear to the first class?',
    a: "Comfortable athletic clothes and bare feet are perfect for the trial. Once your child enrolls, we\u2019ll provide the traditional karate uniform (gi) free with the trial offer.",
  },
  {
    q: 'How often should students come to class?',
    a: "Most students train 2 times per week during our Tuesday and Thursday sessions (4:30 PM \u2013 8:00 PM). Consistency matters more than volume \u2014 twice a week builds real skill and belt progression over time.",
  },
  {
    q: 'How does the belt ranking system work?',
    a: "Students progress from White through Yellow, Orange, Purple, Blue, Green, and three Brown ranks, then earn their Black Belt \u2014 followed by lifelong Dan (degree) ranks. Belts are earned through testing that reviews technique, character, and dedication. Belts are earned, never given.",
  },
  {
    q: 'Where are you located, and is there parking?',
    a: "We\u2019re at " + SCHOOL.address + " \u2014 our Revolution location in northeast Colorado Springs, near Montebello. Free parking is available on-site, and families are always welcome to stay and watch class.",
  },
  {
    q: 'Can parents watch class?',
    a: "Yes, and we love it. Universal Kempo Karate has always been a family-first school. Parents are welcome to stay, watch, and be part of their child\u2019s progress. Many of our families train together.",
  },
];

const FAQ = () => {
  const { openLeadModal } = useLeadModal();

  // Inject FAQPage JSON-LD once for SEO
  useEffect(() => {
    const scriptId = 'faq-jsonld';
    if (document.getElementById(scriptId)) return;
    const data = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    };
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.id = scriptId;
    s.text = JSON.stringify(data);
    document.head.appendChild(s);
    return () => {
      const existing = document.getElementById(scriptId);
      if (existing) existing.remove();
    };
  }, []);

  return (
    <section
      id="faq"
      className="relative py-16 sm:py-20"
      data-testid="faq-section"
      aria-labelledby="faq-heading"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Left: heading + CTA */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-5"
        >
          <span className="font-display uppercase tracking-[0.22em] text-[11px] text-[color:var(--neon-red)] flex items-center gap-2">
            <HelpCircle className="h-3.5 w-3.5" /> Parent Questions
          </span>
          <h2
            id="faq-heading"
            className="mt-3 font-display text-3xl sm:text-4xl text-white tracking-tight leading-tight"
          >
            Everything parents ask
            <br />
            <span className="text-white/70">before their first class.</span>
          </h2>
          <p className="mt-5 text-white/70 leading-relaxed">
            No pressure, no sales pitch — just real answers from a Colorado Springs karate
            school that has trained families for over 40 years.
          </p>

          <div className="mt-6 hidden lg:flex flex-col gap-3">
            <button
              type="button"
              onClick={() => openLeadModal('faq-primary')}
              data-testid="faq-book-trial-button"
              className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl bg-[color:var(--neon-red)] hover:bg-[#ff3a3a] text-white font-display uppercase tracking-[0.14em] text-sm glow-red-sm"
            >
              <Sparkles className="h-4 w-4" /> Book a Free Trial
            </button>
            <a
              href={SCHOOL.phoneHref}
              data-testid="faq-call-link"
              className="text-white/75 hover:text-white text-sm font-mono-ui"
            >
              Or call {SCHOOL.phone}
            </a>
          </div>
        </motion.div>

        {/* Right: Accordion */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.6, delay: 0.05 }}
          className="lg:col-span-7"
        >
          <div className="panel rounded-2xl p-2 sm:p-4">
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={f.q}
                  value={`faq-${i}`}
                  className="border-b border-white/8 last:border-b-0"
                  data-testid={`faq-item-${i}`}
                >
                  <AccordionTrigger
                    className="px-3 py-4 text-left text-white font-display tracking-tight hover:no-underline text-sm sm:text-base"
                    data-testid={`faq-trigger-${i}`}
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent
                    className="px-3 pb-4 text-white/70 leading-relaxed text-sm"
                    data-testid={`faq-content-${i}`}
                  >
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* Mobile CTA under accordion */}
          <div className="mt-6 flex lg:hidden flex-col gap-3">
            <button
              type="button"
              onClick={() => openLeadModal('faq-primary-mobile')}
              data-testid="faq-book-trial-button-mobile"
              className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl bg-[color:var(--neon-red)] hover:bg-[#ff3a3a] text-white font-display uppercase tracking-[0.14em] text-sm glow-red-sm"
            >
              <Sparkles className="h-4 w-4" /> Book a Free Trial
            </button>
            <a
              href={SCHOOL.phoneHref}
              className="text-center text-white/75 hover:text-white text-sm font-mono-ui"
            >
              Or call {SCHOOL.phone}
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;
